import { useRef, useState } from 'react';
import { Card, Field, Pill, useToast } from '../../../components/ui';
import type { BannerAppearance, BannerTemplateIssue } from '../../../lib/api';
import { TEMPLATE_LIMITS, TEMPLATE_SLOTS, type TemplateSlot } from '../../../lib/template-rules';
import { DESIGN_NAME, type CardProps } from './defaults';

type Template = BannerAppearance['template'];

/**
 * The template editor: HTML and CSS side by side with the live preview, the
 * slot reference above, the issues below. Every keystroke goes into the
 * draft, so the preview follows; Apply keeps it, Discard changes puts back
 * what was there when the editor opened.
 */

const SLOT_PHRASE: Record<TemplateSlot, string> = {
  title: 'The heading',
  body: 'The notice text',
  tenant: 'The organisation name',
  buttons: 'Accept all, Reject all, Customise',
  categories: 'The category switches',
  notice_link: 'The cookie notice link',
  language: 'The language control',
  accessibility: 'The accessibility controls',
  logo: 'The logo, when set',
  close: 'The close control',
  powered_by: 'The "by Wekalp" line',
};

const LAYERS: Array<[string, string]> = [
  ['data-wk-layer="ask"', 'Marks the first layer'],
  ['data-wk-layer="prefs"', 'Marks the preferences layer'],
];

/** A text area with a line-number gutter that scrolls with it. */
function Code({ label, value, limit, readOnly, onChange }: {
  label: string; value: string; limit: number; readOnly: boolean; onChange: (v: string) => void;
}) {
  const gutter = useRef<HTMLDivElement>(null);
  const lines = value.split('\n').length;
  return (
    <div className="ck-code">
      <div className="ck-code-head">
        <span>{label}</span>
        <span className={`muted${value.length > limit ? ' ck-over' : ''}`}>{value.length.toLocaleString()} of {limit.toLocaleString()}</span>
      </div>
      <div className="ck-code-body">
        <div ref={gutter} className="ck-code-gutter" aria-hidden="true">
          {Array.from({ length: lines }, (_, i) => <span key={i}>{i + 1}</span>)}
        </div>
        <textarea
          className="mono" value={value} readOnly={readOnly} spellCheck={false} wrap="off" aria-label={label}
          onChange={(e) => onChange(e.target.value)}
          onScroll={(e) => { if (gutter.current) gutter.current.scrollTop = e.currentTarget.scrollTop; }}
        />
      </div>
    </div>
  );
}

export default function DesignEditor({ a, update, readOnly, issues, checking, previewIssues, onApply, onDiscard, onReset }: CardProps & {
  /** Template rule issues, local and server, merged. */
  issues: BannerTemplateIssue[];
  checking: boolean;
  /** What the preview measured on the last render. */
  previewIssues: BannerTemplateIssue[];
  onApply: () => void;
  onDiscard: () => void;
  onReset: () => void;
}) {
  const toast = useToast();
  const t = a.template;
  const set = (patch: Partial<Template>) => update((x) => ({ ...x, template: { ...x.template, ...patch } }));
  const [showSlots, setShowSlots] = useState(true);
  const origin = t.base === 'custom' ? null : DESIGN_NAME[t.base];

  const copy = (text: string) => navigator.clipboard.writeText(text).then(() => toast('Copied.', 'ok'), () => toast('Copy failed.', 'bad'));

  return (
    <Card title="Edit the design">
      <div className="ck-editor-head">
        <Field label="Name" help={origin ? `Started from ${origin}.` : 'Markup and style of your own.'}>
          <input type="text" value={t.name} maxLength={60} readOnly={readOnly} placeholder={origin ?? 'Custom design'} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <div className="ck-editor-actions">
          <button onClick={onReset} disabled={readOnly}>Reset to built-in</button>
          <button onClick={onDiscard}>Discard changes</button>
          <button className="primary" onClick={onApply} disabled={readOnly}>Apply</button>
        </div>
      </div>

      <details className="ck-slots" open={showSlots} onToggle={(e) => setShowSlots(e.currentTarget.open)}>
        <summary>Slots and layers</summary>
        <div className="ck-slot-grid">
          {(Object.keys(TEMPLATE_SLOTS) as TemplateSlot[]).map((s) => (
            <div key={s} className="ck-slot">
              <code>{`{{${s}}}`}</code>
              <span>{SLOT_PHRASE[s]}</span>
              <button className="small" onClick={() => void copy(`{{${s}}}`)}>Copy</button>
            </div>
          ))}
          {LAYERS.map(([attr, phrase]) => (
            <div key={attr} className="ck-slot">
              <code>{attr}</code>
              <span>{phrase}</span>
              <button className="small" onClick={() => void copy(attr)}>Copy</button>
            </div>
          ))}
        </div>
      </details>

      <Code label="HTML" value={t.html} limit={TEMPLATE_LIMITS.html} readOnly={readOnly} onChange={(html) => set({ html })} />
      <Code label="CSS" value={t.css} limit={TEMPLATE_LIMITS.css} readOnly={readOnly} onChange={(css) => set({ css })} />

      <div className="ck-issues">
        <div className="ck-issues-head">
          <span>Issues</span>
          {issues.length > 0 && <Pill tone="bad">{issues.length} in the template</Pill>}
          {previewIssues.length > 0 && <Pill tone="warn">{previewIssues.length} in the preview</Pill>}
          {issues.length === 0 && previewIssues.length === 0 && !checking && <Pill tone="ok">None</Pill>}
          {checking && <span className="muted small">Checking…</span>}
          <span className="muted small ck-issues-note">Template issues block Save and Publish; preview issues block Publish.</span>
        </div>
        {(issues.length > 0 || previewIssues.length > 0) && (
          <ul className="ck-issue-list">
            {issues.map((i) => (
              <li key={`t-${i.code}`}>
                {i.where && <span className="ck-issue-where">{i.where.toUpperCase()}</span>}
                {i.message}
              </li>
            ))}
            {previewIssues.map((i) => <li key={`p-${i.code}`}><span className="ck-issue-where">Preview</span>{i.message}</li>)}
          </ul>
        )}
      </div>
    </Card>
  );
}
